/**
 * Domain-event catalog registry (commerce-platform Phase 0, plan step 12).
 *
 * This module owns the MECHANISM only: the `EventDefinition` shape, the
 * naming regex, duplicate detection, the version store, and lookup helpers.
 * Domain files (`platform.ts`, `commerce.ts`) build their definitions through
 * `defineEvent()`; the composed `EVENT_CATALOG` lives in the catalog barrel
 * (`./index.ts`), assembled by spreading the domain records. The barrel must
 * be imported (directly or via `@cluster/types`) before the catalog is
 * populated — that is the module the services consume.
 *
 * NAMING RULE (master plan assumption A4, code-verified):
 * - NEW commerce-platform events use kebab `<source>:<kebab-event>`
 *   (e.g. `commerce:probe-created`, `orders:order-created`).
 * - EXISTING platform events keep their published dotted names AS-IS
 *   (e.g. `organizations:org.created` from `OrganizationsEventType.ORG_CREATED`;
 *   ~37 dotted literals live under `src/enums/`). The regex below accepts BOTH
 *   forms so existing events can be registered too.
 * - BINDING: any trigger referencing an EXISTING event must use its exact
 *   published name (the Phase 1 onboarding saga trigger is exactly
 *   `organizations:org.created`, never a renamed variant).
 *
 * SCHEMA NOTE: the payload schema is the structural `EventSchema` interface
 * below — a zod `ZodType` satisfies it as-is (`parse(data: unknown): T`).
 * `zod` is deliberately NOT imported here: it is not a dependency of
 * `@cluster/types` and Phase 0 forbids dependency changes. When zod is added
 * to this package by a later, explicit change, domain files may pass real
 * `z.object(...)` schemas through `defineEvent()` with zero consumer edits.
 * See docs/events/catalog.md.
 */

export interface EventSchema<TPayload = unknown> {
  parse(data: unknown): TPayload;
}

export interface EventDefinition<TPayload = unknown> {
  /** Published event type, e.g. `commerce:probe-created` or `organizations:org.created`. */
  type: string;
  /** Definition version, integer >= 1. Additive-only within a version; breaking changes register a new version alongside the old one. */
  version: number;
  /** Structural (zod-compatible) payload schema. */
  schema: EventSchema<TPayload>;
  /** When true the audit service records a row per occurrence; false means event is observability-only for audit. */
  auditable: boolean;
  description: string;
  /** Catalog release this definition was introduced in (catalog semver, e.g. `1.0.0`). */
  since: string;
}

/**
 * Accepts new kebab names (`commerce:probe-created`) AND legacy published
 * dotted names (`organizations:org.created`, `agent-factory:model.test-ok`).
 */
export const EVENT_TYPE_PATTERN = /^[a-z0-9-]+:[a-z0-9-]+([.-][a-z0-9-]+)*$/;

/**
 * Draft dotted names -> reserved catalog names (master A4). The design-doc
 * drafts used bare dotted names; at implementation each becomes a
 * `<source>:<kebab-event>` type whose right-hand name is RESERVED for that
 * domain. This table maps drafts only — already-published platform events
 * (left examples with a source prefix, e.g. `organizations:org.created`)
 * keep their published names AS-IS and are never remapped.
 */
export const RESERVED_LEGACY_NAME_MAP: Readonly<Record<string, string>> = {
  'order.created': 'orders:order-created',
  'payment.captured': 'payments:payment-captured',
  'seat.held': 'ticketing:seat-held',
  'refund.issued': 'orders:refund-issued',
  // P3-T6 reconciliation: the draft alias `subscriptions:subscription-suspended`
  // is superseded — the dunning machine (services/subscriptions) emits the bare
  // `subscriptions:suspended` and catalog/subscriptions.ts registers it as the
  // single canonical truth.
  'subscription.suspended': 'subscriptions:suspended',
};

// type -> (version -> definition). Populated by defineEvent() as domain
// modules are evaluated; duplicate (type, version) pairs throw at build time.
const definitionStore = new Map<string, Map<number, EventDefinition>>();

function assertValidDefinition(input: EventDefinition): void {
  if (typeof input.type !== 'string' || !EVENT_TYPE_PATTERN.test(input.type)) {
    throw new Error(
      `Invalid event type "${String(input.type)}": must match ${EVENT_TYPE_PATTERN.source} ` +
        `(kebab <source>:<kebab-event> or legacy <source>:<dotted.name>)`,
    );
  }
  if (!Number.isInteger(input.version) || input.version < 1) {
    throw new Error(`Invalid version for event "${input.type}": expected integer >= 1, got ${String(input.version)}`);
  }
  if (input.schema == null || typeof input.schema.parse !== 'function') {
    throw new Error(`Event "${input.type}" v${input.version} requires a schema with a parse(data) function`);
  }
  if (typeof input.auditable !== 'boolean') {
    throw new Error(`Event "${input.type}" v${input.version} requires a boolean auditable flag`);
  }
  if (typeof input.description !== 'string' || input.description.trim() === '') {
    throw new Error(`Event "${input.type}" v${input.version} requires a non-empty description`);
  }
  if (typeof input.since !== 'string' || input.since.trim() === '') {
    throw new Error(`Event "${input.type}" v${input.version} requires a non-empty since field`);
  }
}

/**
 * Validates an event definition (naming regex + required fields), stores it
 * for versioned lookup, and returns it for domain-record assembly.
 * Throws on a malformed name or a duplicate (type, version) registration —
 * duplicate detection fires when the catalog barrel is first imported, i.e.
 * at registry build.
 */
export function defineEvent<TPayload>(
  definition: EventDefinition<TPayload>,
): EventDefinition<TPayload> {
  assertValidDefinition(definition);
  const versions = definitionStore.get(definition.type) ?? new Map<number, EventDefinition>();
  if (versions.has(definition.version)) {
    throw new Error(
      `Duplicate event registration: "${definition.type}" version ${definition.version} is already registered`,
    );
  }
  versions.set(definition.version, definition as EventDefinition);
  definitionStore.set(definition.type, versions);
  return definition;
}

/**
 * Looks up a registered definition. Without `version`, returns the highest
 * registered version for the type (the active definition). Exact legacy
 * dotted types (`getEventDefinition('organizations:org.created')`) resolve
 * only if a domain file has registered them.
 */
export function getEventDefinition(
  type: string,
  version?: number,
): EventDefinition | undefined {
  const versions = definitionStore.get(type);
  if (!versions || versions.size === 0) return undefined;
  if (version !== undefined) return versions.get(version);
  let active: EventDefinition | undefined;
  for (const def of versions.values()) {
    if (!active || def.version > active.version) active = def;
  }
  return active;
}

// ---------------------------------------------------------------------------
// Payload-schema primitives (structural, zod-compatible semantics: unknown
// extra fields are ignored; a missing/invalid required field throws).
// ---------------------------------------------------------------------------

export function asEventRecord(data: unknown, context: string): Record<string, unknown> {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    throw new Error(`${context}: payload must be a plain object`);
  }
  return data as Record<string, unknown>;
}

export function requireString(value: unknown, field: string, context: string): string {
  if (typeof value !== 'string' || value === '') {
    throw new Error(`${context}: field "${field}" must be a non-empty string`);
  }
  return value;
}

export function optionalString(value: unknown, field: string, context: string): string | undefined {
  if (value === undefined || value === null) return undefined;
  return requireString(value, field, context);
}

export function requireStringArray(value: unknown, field: string, context: string): string[] {
  if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) {
    throw new Error(`${context}: field "${field}" must be an array of strings`);
  }
  return value as string[];
}

export function optionalNumber(value: unknown, field: string, context: string): number | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${context}: field "${field}" must be a finite number`);
  }
  return value;
}
